<template>
  <v-container fluid class="py-8">
    <!-- Header -->
    <v-row class="mb-4" align="center" justify="space-between">
      <v-col cols="auto">
        <h1 class="text-h5 font-weight-bold">Email Templates</h1>
      </v-col>
      <v-col cols="auto">
        <v-btn color="success" @click="openNewTemplateModal" :disabled="isLoading">
          + Create New Template
        </v-btn>
      </v-col>
    </v-row>

    <!-- Loading skeleton (table placeholder) -->
    <v-card v-if="isLoading" elevation="2" class="mb-6">
      <v-card-title>Email Templates</v-card-title>
      <v-card-text>
        <!-- Header row skeleton -->
        <div class="d-flex justify-space-between align-center mb-4">
          <v-skeleton-loader type="text" width="30%" />
          <v-skeleton-loader type="chip" />
        </div>

        <!-- Table head skeleton -->
        <div class="d-flex mb-2">
          <v-skeleton-loader type="text" width="20%" class="mr-6" />
          <v-skeleton-loader type="text" width="15%" class="mr-6" />
          <v-skeleton-loader type="text" width="15%" class="mr-6" />
          <v-skeleton-loader type="text" width="30%" class="mr-6" />
          <v-skeleton-loader type="text" width="10%" class="ml-auto" />
        </div>

        <!-- Rows skeleton -->
        <div v-for="n in pageSize" :key="n" class="d-flex align-center py-3">
          <v-skeleton-loader type="text" width="20%" class="mr-6" />
          <v-skeleton-loader type="text" width="15%" class="mr-6" />
          <v-skeleton-loader type="text" width="15%" class="mr-6" />
          <v-skeleton-loader type="text" width="30%" class="mr-6" />
          <div class="ml-auto d-flex ga-2">
            <v-skeleton-loader type="button" width="80" />
            <v-skeleton-loader type="button" width="80" />
          </div>
        </div>

        <!-- Pagination skeleton -->
        <div class="d-flex justify-end mt-4">
          <v-skeleton-loader type="chip" class="mr-2" />
          <v-skeleton-loader type="chip" />
        </div>
      </v-card-text>
    </v-card>

    <!-- Real table -->
    <v-card v-else elevation="2" class="mb-6">
      <v-card-title>Email Templates</v-card-title>
      <v-card-text>
        <v-table>
          <thead>
            <tr>
              <th>Label</th>
              <th>Target</th>
              <th>Code</th>
              <th>Subject</th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="template in paginatedTemplates" :key="template.id">
              <td>{{ template.label }}</td>
              <td>{{ template.audience }}</td>
              <td><code>{{ template.code }}</code></td>
              <td>{{ template.subject }}</td>
              <td class="text-right">
                <v-btn size="small" variant="outlined" color="primary" class="mr-2" @click="editTemplate(template)">
                  Edit
                </v-btn>
                <v-btn size="small" variant="outlined" color="error" @click="confirmDelete(template)">
                  Delete
                </v-btn>
              </td>
            </tr>
            <tr v-if="filteredTemplates.length === 0">
              <td colspan="5" class="text-center text-grey">No templates found.</td>
            </tr>
          </tbody>
        </v-table>

        <!-- Pagination -->
        <v-row class="mt-4" align="center" justify="space-between">
          <v-col cols="auto">
            <small class="text-muted">
              Showing {{ pageStart + 1 }}–{{ pageEnd }} of {{ filteredTemplates.length }}
            </small>
          </v-col>
          <v-col cols="auto">
            <v-pagination v-model="currentPage" :length="totalPages" :total-visible="5" prev-icon="mdi-chevron-left"
              next-icon="mdi-chevron-right" />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Template Modal -->
    <v-dialog v-model="showTemplateModal" max-width="1600">
      <v-card v-if="selectedTemplate" class="pa-6">
        <v-card-title class="text-h6 font-weight-bold">
          {{ selectedTemplate.id ? 'Edit Template' : 'Create New Template' }}
        </v-card-title>

        <v-card-text>
          <v-row>
            <!-- Editor Panel -->
            <v-col cols="12" md="6">
              <v-text-field v-model="selectedTemplate.code" label="Code" variant="outlined"
                :disabled="!!selectedTemplate.id" class="mb-4" />
              <v-text-field v-model="selectedTemplate.label" label="Label" variant="outlined" class="mb-4" />

              <v-row class="mb-4">
                <v-col cols="12" sm="6">
                  <v-select v-model="selectedTemplate.audience" :items="['agent', 'consumer', 'internal']"
                    label="Audience" variant="outlined" clearable />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select v-model="selectedTemplate.trigger_event" :items="triggerEventOptions" item-title="label"
                    item-value="value" label="Trigger Event" variant="outlined" clearable />
                </v-col>
              </v-row>

              <!-- Subject with token insert -->
              <v-row class="mb-4" align="center">
                <v-col cols="9">
                  <v-text-field v-model="selectedTemplate.subject" label="Email Subject" variant="outlined"
                    ref="subjectInput" />
                </v-col>
                <v-col cols="3">
                  <v-menu offset-y>
                    <template #activator="{ props }">
                      <v-btn v-bind="props" variant="outlined" block size="small">
                        Insert Token
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item @click="insertTokenIntoSubject('{{name}}')">User Name</v-list-item>
                      <v-list-item @click="insertTokenIntoSubject('{{ship}}')">Ship Name</v-list-item>
                      <v-list-item @click="insertTokenIntoSubject('{{sailDate}}')">Sail Date</v-list-item>
                      <v-list-item @click="insertTokenIntoSubject('{{confirmationCode}}')">Confirmation
                        Code</v-list-item>
                      <v-divider />
                      <v-list-item @click="insertLogo">Insert Logo</v-list-item>
                    </v-list>
                  </v-menu>
                </v-col>
              </v-row>

              <!-- HTML Template with token insert -->
              <v-row class="mb-2" align="center">
                <v-col>
                  <span class="text-body-2 font-weight-medium">HTML Template</span>
                </v-col>
                <v-col cols="auto">
                  <v-menu offset-y>
                    <template #activator="{ props }">
                      <v-btn v-bind="props" variant="outlined" size="small">Insert Token</v-btn>
                    </template>
                    <v-list>
                      <v-list-item @click="insertToken('{{name}}', 'textarea')">User Name</v-list-item>
                      <v-list-item @click="insertToken('{{ship}}', 'textarea')">Ship Name</v-list-item>
                      <v-list-item @click="insertToken('{{sailDate}}', 'textarea')">Sail Date</v-list-item>
                      <v-list-item @click="insertToken('{{confirmationCode}}', 'textarea')">Confirmation
                        Code</v-list-item>
                      <v-divider />
                      <v-list-item @click="insertLogo">Insert Logo</v-list-item>
                    </v-list>
                  </v-menu>
                </v-col>
              </v-row>

              <v-textarea v-model="selectedTemplate.html_template" auto-grow rows="18" variant="outlined"
                class="font-monospace" />
            </v-col>

            <!-- Live Preview -->
            <v-col cols="12" md="6">
              <h6 class="mb-2">Live Preview</h6>
              <div class="border p-3 bg-grey-lighten-4 rounded" style="height: 100%; min-height: 500px; overflow: auto;"
                v-html="filledPreview" />
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" @click="saveTemplate">Save</v-btn>
          <v-btn variant="outlined" @click="showTemplateModal = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm Delete Modal -->
    <v-dialog v-model="showDeleteConfirm" max-width="400">
      <v-card>
        <v-card-title class="text-h6 text-danger">Confirm Deletion</v-card-title>
        <v-card-text>
          Are you sure you want to delete <strong>{{ templateToDelete?.label }}</strong>?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="outlined" @click="showDeleteConfirm = false">Cancel</v-btn>
          <v-btn color="error" @click="deleteTemplateConfirmed">Yes, Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" location="bottom right" timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>

  </v-container>
</template>

<script>
import { supabase } from '@/supabaseClient'

export default {
  data() {
    return {
      templates: [],
      selectedTemplate: null,
      previewVars: {
        name: 'Alex',
        ship: 'Sun Princess',
        sailDate: '2025-08-10',
        confirmationCode: 'ABC123'
      },
      search: '',
      currentPage: 1,
      pageSize: 5,
      templateToDelete: null,
      isLoading: true,
      showTemplateModal: false,
      showDeleteConfirm: false,
      triggerEventOptions: [
        { label: 'Registration Confirmed', value: 'registration_confirmed' },
        { label: 'Booking Approved', value: 'booking_approved' },
        { label: 'Booking Denied', value: 'booking_denied' },
        { label: 'Payment Reminder', value: 'payment_reminder' },
        { label: 'Payment Confirmed', value: 'payment_confirmed' }
      ],
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      }
    }
  },
  computed: {
    filledPreview() {
      if (!this.selectedTemplate) return ''
      return this.fillTemplate(this.selectedTemplate.html_template)
    },
    filteredTemplates() {
      const s = (this.search || '').toLowerCase()
      return this.templates.filter(t => (t.label || '').toLowerCase().includes(s))
    },
    totalPages() {
      return Math.ceil(this.filteredTemplates.length / this.pageSize) || 1
    },
    paginatedTemplates() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.filteredTemplates.slice(start, start + this.pageSize)
    },
    pageStart() {
      return (this.currentPage - 1) * this.pageSize
    },
    pageEnd() {
      return Math.min(this.pageStart + this.pageSize, this.filteredTemplates.length)
    }
  },
  methods: {
    showSnackbar(message, color = 'success') {
      this.snackbar.message = message
      this.snackbar.color = color
      this.snackbar.show = true
    },
    async fetchTemplates() {
      this.isLoading = true
      try {
        const { data, error } = await supabase
          .from('email_templates')
          .select('*') // PostgREST supports AbortController
          .order('label')

        if (error) throw error
        this.templates = data ?? []
      } catch (err) {
        // If it was an abort, don't toast; but DO end loading
        if (err?.name !== 'AbortError') {
          console.error('fetchTemplates failed:', err)
          this.templates = []
        }
      } finally {
        this.isLoading = false
      }
    },
    openNewTemplateModal() {
      this.selectedTemplate = {
        id: null,
        code: '',
        label: '',
        subject: '',
        html_template: '',
        trigger_event: '',
        audience: ''
      }
      this.showTemplateModal = true
    },
    editTemplate(template) {
      this.selectedTemplate = { ...template }
      this.showTemplateModal = true
    },
    fillTemplate(template) {
      if (!template) return ''
      return template.replace(/\{\{(\w+)\}\}/g, (_, key) => this.previewVars[key] || '')
    },
    insertToken(token, selector = 'textarea') {
      // Insert into the html_template field (works with the textarea)
      const el = this.$el.querySelector(selector)
      if (!el) return
      const start = el.selectionStart ?? 0
      const end = el.selectionEnd ?? 0
      const text = this.selectedTemplate.html_template || ''
      this.selectedTemplate.html_template = text.substring(0, start) + token + text.substring(end)
      this.$nextTick(() => el.focus())
    },
    insertTokenIntoSubject(token) {
      const input = this.$refs.subjectInput?.$el?.querySelector('input')
      if (!input) return
      const start = input.selectionStart ?? 0
      const end = input.selectionEnd ?? 0
      const text = this.selectedTemplate.subject || ''
      this.selectedTemplate.subject = text.substring(0, start) + token + text.substring(end)
      this.$nextTick(() => input.focus())
    },
    insertLogo() {
      // NOTE: fixed double slash in your original URL
      const logoHtml =
        `<img
          src="https://dumbslepwdosoyfhijdh.supabase.co/storage/v1/object/public/email-assets/logo-email.png"
          alt="Unbooked"
          style="width: 100%; max-width: 600px; height: auto; display: block; margin: auto; padding: 24px;" />`
      this.insertToken(logoHtml)
    },
    confirmDelete(template) {
      this.templateToDelete = template
      this.showDeleteConfirm = true
    },
    async deleteTemplateConfirmed() {
      if (!this.templateToDelete) return
      const { error } = await supabase
        .from('email_templates')
        .delete()
        .eq('id', this.templateToDelete.id)

      if (error) {
        alert('Failed to delete template.')
        console.error(error)
      } else {
        await this.fetchTemplates()
        // Fix pagination edge case
        const maxPage = Math.ceil(this.filteredTemplates.length / this.pageSize) || 1
        if (this.currentPage > maxPage) this.currentPage = maxPage
      }
      this.templateToDelete = null
      this.showDeleteConfirm = false
    },
    async saveTemplate() {
      const t = this.selectedTemplate
      if (!t.code || !t.label || !t.subject || !t.html_template) {
        this.showSnackbar('All fields are required.', 'error')
        return
      }

      if (t.id) {
        const { error } = await supabase
          .from('email_templates')
          .update({
            label: t.label,
            subject: t.subject,
            html_template: t.html_template,
            trigger_event: t.trigger_event,
            audience: t.audience,
            updated_at: new Date().toISOString()
          })
          .eq('id', t.id)

        if (error) {
          console.error(error)
          this.showSnackbar('Failed to update template.', 'error')
          return
        }
        this.showSnackbar('Template updated successfully.', 'success')
      } else {
        const { error } = await supabase
          .from('email_templates')
          .insert([{
            code: t.code,
            label: t.label,
            subject: t.subject,
            html_template: t.html_template,
            trigger_event: t.trigger_event,
            audience: t.audience
          }])

        if (error) {
          console.error(error)
          this.showSnackbar('Failed to create template.', 'error')
          return
        }
        this.showSnackbar('New template created successfully.', 'success')
      }

      await this.fetchTemplates()
      this.showTemplateModal = false
      this.selectedTemplate = null
    }
  },
  mounted() {
    this.fetchTemplates()
  },
}
</script>

<style scoped>
.font-monospace {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, "Liberation Mono",
    "Courier New", monospace;
}
</style>
